import { useState } from "react";
import { submitWaitlistLead, goToClosedForm } from "../../lib/penaltyRecoveryApi";

const SERIF = "'Playfair Display',Georgia,serif";
const MONO = "'IBM Plex Mono',monospace";
const INPUT_STYLE = {
  width: "100%", padding: "13px 15px", border: "1px solid #DED9CC", borderRadius: 8,
  font: "inherit", fontSize: 15, color: "#233853", background: "#FCFBF8", outline: "none",
};

/** Prominent closure banner rendered directly under the sticky nav. */
export function ClosedBanner() {
  return (
    <div data-testid="pr-closed-banner" style={{ background: "#233853", borderBottom: "3px solid #C8AC43" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "16px 36px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "8px 18px", textAlign: "center" }}>
        <span style={{ fontFamily: MONO, fontSize: 11.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "#C8AC43", fontWeight: 600, whiteSpace: "nowrap" }}>Notice · June 2026</span>
        <span style={{ color: "#D7DDE4", fontSize: 15, lineHeight: 1.5 }}>
          We are <strong style={{ color: "#fff", fontWeight: 600 }}>no longer accepting new applications</strong> — the time remaining before the filing deadline is too short to responsibly prepare new claims. <strong style={{ color: "#fff", fontWeight: 600 }}>Existing clients: your application is still being processed.</strong>
        </span>
        <button onClick={goToClosedForm} data-testid="pr-closed-banner-link" style={{ background: "none", border: "none", font: "inherit", color: "#fff", fontWeight: 600, fontSize: 14.5, cursor: "pointer", borderBottom: "1px solid #C8AC43", paddingBottom: 2, whiteSpace: "nowrap" }}>
          Still need a CPA? ↓
        </button>
      </div>
    </div>
  );
}

/** Closure explanation + name/email/phone capture. Replaces the paid checkout path. */
export function ClosedContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 7;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      await submitWaitlistLead({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() });
      setDone(true);
    } catch (e) {
      setError(e.message || "Could not save your details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="pr-closed" data-testid="pr-closed-section" style={{ background: "#FAF8F3", borderBottom: "1px solid #EFEBE0", scrollMarginTop: 90 }}>
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "84px 36px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 13, fontWeight: 600, letterSpacing: "0.13em", color: "#B5993F", textTransform: "uppercase", marginBottom: 22 }}>
          <span style={{ width: 34, height: 1.5, background: "#B5993F" }} />An honest update
        </div>
        <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 42, lineHeight: 1.12, color: "#233853", marginBottom: 18, textWrap: "balance" }}>
          We've stopped accepting <span style={{ fontStyle: "italic", color: "#9A7E25" }}>new</span> applications.
        </h2>
        <p style={{ fontSize: 17.5, color: "#5C6776", lineHeight: 1.62, maxWidth: 680, marginBottom: 16 }}>
          Preparing a claim properly takes time — a CPA has to pull your IRS transcripts, verify every penalty line by line, and prepare the filing. With the submission deadline this close, there isn't enough time left to responsibly complete that work for new clients. Rather than take a payment for work we can't stand behind, we've closed new applications.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 16, background: "#233853", borderRadius: 12, padding: "20px 26px", margin: "26px 0 34px" }}>
          <span style={{ flexShrink: 0, width: 38, height: 38, borderRadius: "50%", background: "rgba(0,164,191,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#3FC6DD", fontSize: 19, fontWeight: 700 }}>✓</span>
          </span>
          <p style={{ fontSize: 16.5, color: "#fff", lineHeight: 1.5, fontWeight: 500 }}>
            <strong style={{ color: "#C8AC43" }}>Already submitted? You're covered.</strong> Every application we've accepted is being processed through completion — nothing changes for existing clients.
          </p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #E7E3D8", borderRadius: 16, padding: 36, boxShadow: "0 20px 50px -30px rgba(22,38,61,0.25)" }}>
          {done ? (
            <div style={{ textAlign: "center", padding: "10px 0" }} data-testid="pr-closed-success">
              <span style={{ display: "inline-flex", width: 52, height: 52, borderRadius: "50%", background: "rgba(0,164,191,0.12)", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                <span style={{ color: "#00A4BF", fontSize: 26, fontWeight: 700 }}>✓</span>
              </span>
              <h3 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 25, color: "#233853", marginBottom: 10 }}>Thanks — we have your details.</h3>
              <p style={{ fontSize: 16, color: "#5C6776", lineHeight: 1.6, maxWidth: 460, margin: "0 auto" }}>
                A KGOB CPA will reach out. Prefer not to wait? Call <a data-testid="penalty-recovery-closed-notice-a-2" href="tel:+18445993355" style={{ color: "#233853", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #CFD4DA" }}>844-599-3355</a>.
              </p>
            </div>
          ) : (
            <div>
              <h3 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 25, color: "#233853", marginBottom: 8 }}>Still need a CPA — or want to talk it through?</h3>
              <p style={{ fontSize: 15.5, color: "#5C6776", lineHeight: 1.6, marginBottom: 24 }}>
                Leave your details and we'll reach out — or call us directly at <a data-testid="penalty-recovery-closed-notice-a-1" href="tel:+18445993355" style={{ color: "#233853", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #CFD4DA" }}>844-599-3355</a> or email <a data-testid="penalty-recovery-closed-email" href="mailto:connect@kgob.com" style={{ color: "#233853", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #CFD4DA" }}>connect@kgob.com</a>.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }} data-closed-grid="">
                <input type="text" placeholder="Full name" value={form.name} onChange={set("name")} data-testid="pr-closed-name" style={INPUT_STYLE} />
                <input type="tel" placeholder="Phone number" value={form.phone} onChange={set("phone")} data-testid="pr-closed-phone" style={INPUT_STYLE} />
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                <input type="email" placeholder="you@email.com" value={form.email} onChange={set("email")} data-testid="pr-closed-email" style={{ ...INPUT_STYLE, flex: "1 1 260px", width: "auto" }} />
                <button onClick={submit} disabled={!valid || saving} data-testid="pr-closed-submit" style={{ background: valid ? "linear-gradient(180deg,#C8AC43,#B08C24)" : "#EAE6DA", color: "#233853", font: "inherit", fontWeight: 700, fontSize: 15.5, padding: "13px 26px", border: "none", borderRadius: 8, cursor: valid && !saving ? "pointer" : "default", opacity: saving ? 0.7 : 1, whiteSpace: "nowrap" }}>
                  {saving ? "Sending…" : "Request a call back"}
                </button>
              </div>
              {error && <p style={{ fontSize: 14, color: "#B4472F", marginTop: 12 }} data-testid="pr-closed-error">{error}</p>}
              <p style={{ fontSize: 13, color: "#9098A4", marginTop: 16, lineHeight: 1.55 }}>
                We only use this to contact you about your tax situation — no marketing lists, no sharing.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
